// The realtime connection's state machine (spec 18 § 12, spec 05 § 2): ticket → socket → hello → welcome → live, with
// full-jitter backoff, resume from the last seq of each stream, `resync`, the 20 s ack heartbeat and the close codes.
// Everything it touches comes in through `RealtimeDeps` (a ticket, a socket, the topics, the effects), so the Vitest
// tests drive it on fake timers; `connection.ts` wires the real ones.
import { type RealtimeAction, routeTopic } from './topicRouter'

export type RealtimeState = 'idle' | 'ticketing' | 'connecting' | 'syncing' | 'live' | 'backoff' | 'stopped'

/** The part of a WebSocket the client uses; `connection.ts` adapts the browser's. */
export interface SocketLike {
  send(data: string): void
  close(code?: number): void
  onopen: (() => void) | null
  onmessage: ((data: string) => void) | null
  onclose: ((code: number) => void) | null
}

export interface RealtimeDeps {
  /** `POST /stream/ticket`; `url` is the socket's address when the server names one. */
  ticket(): Promise<{ ticket: string; url?: string }>
  /** Opens the socket on a full address (the ticket is already in the query). */
  open(url: string): SocketLike
  /** The topics this person may follow now: their user topics and each organization's. */
  topics(): string[]
  onActions(actions: RealtimeAction[]): void
  /** A stream the server cannot replay: refetch what it shows (the client follows from its head). */
  onResync(stream: string): void
  /** The server ended the session (close 4001): no reconnect. */
  onStop(reason: string): void
  /** Close 4003: nothing permitted any more, so `/me` is read again. */
  onForbidden(): void
  /** In [0, 1): the backoff's jitter. */
  random(): number
}

const BASE_MS = 1_000
const CAP_MS = 30_000
const RATE_LIMITED_MS = 60_000
const SILENCE_MS = 45_000
const DEFAULT_HEARTBEAT_MS = 20_000
const STREAM_PATH = '/api/v1/stream'

export class RealtimeClient {
  state: RealtimeState = 'idle'
  /** The delay the current backoff waits, for the tests and the status line. */
  nextDelayMs = 0

  private socket: SocketLike | undefined
  private generation = 0
  private attempt = 0
  /** Per stream: every seq up to `contiguous` was applied, and `pending` holds the ones applied beyond a gap. */
  private streams = new Map<string, { contiguous: number; pending: Set<number> }>()
  /** Topics the server refused on this socket (`error forbidden`): not asked for again until the socket is new. */
  private refused = new Set<string>()
  private subscribed = new Set<string>()
  private retryTimer: ReturnType<typeof setTimeout> | undefined
  private ackTimer: ReturnType<typeof setInterval> | undefined
  private silenceTimer: ReturnType<typeof setTimeout> | undefined

  constructor(private readonly deps: RealtimeDeps) {}

  start(): void {
    if (this.state !== 'idle' && this.state !== 'stopped') return
    this.attempt = 0
    this.begin()
  }

  stop(): void {
    this.generation++
    this.clearTimers()
    const socket = this.socket
    this.socket = undefined
    this.state = 'idle'
    socket?.close(1000)
  }

  /** The app is in the foreground or the network is back: do not wait out the backoff. */
  retryNow(): void {
    if (this.state !== 'backoff') return
    clearTimeout(this.retryTimer)
    this.begin()
  }

  /** The person's topics changed (a membership came or went): tell the server the difference. */
  topicsChanged(): void {
    if (this.state !== 'live') return
    const next = new Set(this.deps.topics())
    for (const topic of this.refused) if (!next.has(topic)) this.refused.delete(topic)
    const added = [...next].filter((t) => !this.subscribed.has(t) && !this.refused.has(t))
    const removed = [...this.subscribed].filter((t) => !next.has(t))
    if (added.length) this.send({ t: 'subscribe', topics: added })
    if (removed.length) this.send({ t: 'unsubscribe', topics: removed })
    for (const topic of added) this.subscribed.add(topic)
    for (const topic of removed) this.subscribed.delete(topic)
  }

  private begin(): void {
    const generation = ++this.generation
    this.state = 'ticketing'
    this.deps.ticket().then(
      ({ ticket, url }) => {
        if (generation !== this.generation) return
        this.connect(`${url ?? STREAM_PATH}?ticket=${encodeURIComponent(ticket)}`, generation)
      },
      () => {
        if (generation === this.generation) this.backoff(this.jitter())
      },
    )
  }

  private connect(url: string, generation: number): void {
    this.state = 'connecting'
    const socket = this.deps.open(url)
    this.socket = socket
    socket.onopen = () => {
      if (generation !== this.generation) return
      this.state = 'syncing'
      this.closingReason = 'session_revoked'
      this.refused.clear()
      this.subscribed = new Set(this.deps.topics())
      this.send({
        t: 'hello',
        streams: this.resumePoints(),
        topics: [...this.subscribed],
        client: { kind: 'web' },
      })
      this.watchSilence(generation)
    }
    socket.onmessage = (data) => {
      if (generation === this.generation) this.receive(data, generation)
    }
    socket.onclose = (code) => {
      if (generation === this.generation) this.closed(code)
    }
  }

  private receive(data: string, generation: number): void {
    let frame: Record<string, unknown>
    try {
      frame = JSON.parse(data) as Record<string, unknown>
    } catch {
      return
    }
    this.watchSilence(generation)
    switch (frame.t) {
      case 'welcome':
        this.welcomed(frame, generation)
        return
      case 'event':
        this.event(frame)
        return
      case 'resync_required':
        if (typeof frame.stream === 'string') this.deps.onResync(frame.stream)
        return
      case 'closing':
        // The close frame that follows carries the code (4001 for session_revoked).
        if (frame.reconnect === false && typeof frame.reason === 'string') this.closingReason = frame.reason
        return
      case 'error':
        if (frame.code === 'forbidden' && typeof frame.topic === 'string') {
          this.subscribed.delete(frame.topic)
          this.refused.add(frame.topic)
        }
        return
      default:
        return // subscribed, heartbeat, pong
    }
  }

  private closingReason = 'session_revoked'

  private welcomed(frame: Record<string, unknown>, generation: number): void {
    this.state = 'live'
    this.attempt = 0
    const streams = (frame.streams ?? {}) as Record<string, { resume?: string; fromSeq?: number; headSeq?: number }>
    for (const [stream, start] of Object.entries(streams)) {
      if (start.resume === 'resync') {
        if (typeof start.headSeq === 'number')
          this.streams.set(stream, { contiguous: start.headSeq, pending: new Set() })
        this.deps.onResync(stream)
      } else if (typeof start.fromSeq === 'number') {
        // The replay starts here: everything before it is already applied (or was never ours to apply).
        const known = this.streams.get(stream)
        const contiguous = Math.max(known?.contiguous ?? 0, start.fromSeq - 1)
        this.streams.set(stream, { contiguous, pending: known?.pending ?? new Set() })
        this.advance(stream)
      }
    }
    const beat = typeof frame.heartbeatMs === 'number' ? frame.heartbeatMs : DEFAULT_HEARTBEAT_MS
    clearInterval(this.ackTimer)
    this.ackTimer = setInterval(() => {
      if (generation === this.generation) this.send({ t: 'ack', streams: this.resumePoints() })
    }, beat)
  }

  private event(frame: Record<string, unknown>): void {
    const stream = String(frame.stream ?? '')
    const seq = frame.seq
    if (typeof seq === 'number' && !this.markSeen(stream, seq)) return // already applied: live repeats the replay
    const payload = (frame.payload ?? {}) as { orgId?: string }
    const actions = routeTopic(
      String(frame.topic ?? ''),
      typeof frame.type === 'string' ? frame.type : undefined,
      payload.orgId,
    )
    if (actions.length) this.deps.onActions(actions)
  }

  /**
   * Records that `seq` of `stream` is applied; false when it already was. Live events can arrive before the replay
   * that follows `welcome` has delivered the earlier ones, so a seq lower than the latest is still new: only one at or
   * below the contiguous mark, or one already seen beyond a gap, is a repeat.
   */
  private markSeen(stream: string, seq: number): boolean {
    let state = this.streams.get(stream)
    if (!state) {
      state = { contiguous: seq - 1, pending: new Set() } // the first event of a stream no welcome named
      this.streams.set(stream, state)
    }
    if (seq <= state.contiguous || state.pending.has(seq)) return false
    state.pending.add(seq)
    this.advance(stream)
    return true
  }

  private advance(stream: string): void {
    const state = this.streams.get(stream)
    if (!state) return
    while (state.pending.delete(state.contiguous + 1)) state.contiguous++
    for (const seq of state.pending) if (seq <= state.contiguous) state.pending.delete(seq)
  }

  /** What `hello` and `ack` carry: per stream, the seq below which nothing is missing. */
  private resumePoints(): Record<string, number> {
    return Object.fromEntries([...this.streams].map(([stream, state]) => [stream, state.contiguous]))
  }

  private closed(code: number): void {
    this.clearTimers()
    this.socket = undefined
    if (code === 4001) {
      this.state = 'stopped'
      this.deps.onStop(this.closingReason)
      return
    }
    if (code === 4003) this.deps.onForbidden()
    this.backoff(code === 4029 ? RATE_LIMITED_MS : this.jitter())
  }

  /** Full jitter: anywhere from nothing to 1 s doubled per failed attempt, at most 30 s. */
  private jitter(): number {
    const cap = Math.min(CAP_MS, BASE_MS * 2 ** this.attempt++)
    return Math.floor(this.deps.random() * cap)
  }

  private backoff(delayMs: number): void {
    this.state = 'backoff'
    this.nextDelayMs = delayMs
    this.retryTimer = setTimeout(() => this.begin(), delayMs)
  }

  /** No frame for 45 s (two missed heartbeats and a bit): the socket is dead, so close it and come back. */
  private watchSilence(generation: number): void {
    clearTimeout(this.silenceTimer)
    this.silenceTimer = setTimeout(() => {
      if (generation === this.generation) this.socket?.close(4008)
    }, SILENCE_MS)
  }

  private clearTimers(): void {
    clearTimeout(this.retryTimer)
    clearInterval(this.ackTimer)
    clearTimeout(this.silenceTimer)
  }

  private send(frame: Record<string, unknown>): void {
    this.socket?.send(JSON.stringify(frame))
  }
}
